import React from 'react';
import { AlertTriangle, ZapOff, Info } from 'lucide-react';
import { cn } from '../../ui';

/**
 * StockMatrixCell
 *
 * Celda de la matriz "Stock disponible por empresa".
 * El color comunica la severidad del umbral configurado (opción A: la
 * severidad la decide el umbral, no se calcula por la cantidad):
 *
 *   ok        → verde   (por encima del mínimo)
 *   info      → azul    (bajo umbral, severidad info)
 *   warning   → ámbar   (bajo umbral, severidad warning) + icono triángulo
 *   danger    → rojo    (bajo umbral, severidad danger)  + icono zap-off
 *   sin_regla → neutro  (sin regla de alerta configurada)
 *
 * 'critico' se mantiene como alias de 'danger' por retrocompatibilidad.
 *
 * Props:
 *   cantidad: number
 *   estado: 'ok' | 'info' | 'warning' | 'danger' | 'critico' | 'sin_regla'
 *   umbral: number | null   (para el tooltip)
 *   onClick?: () => void
 */

const STYLES = {
  ok: 'bg-emerald-100 text-emerald-800',
  info: 'bg-blue-100 text-blue-800',
  warning: 'bg-amber-100 text-amber-800',
  danger: 'bg-rose-100 text-rose-800',
  critico: 'bg-rose-100 text-rose-800', // alias legacy de danger
  sin_regla: 'text-slate-500',
};

export default function StockMatrixCell({ cantidad, estado, umbral, onClick }) {
  const showWarnIcon = estado === 'warning';
  const showCritIcon = estado === 'danger' || estado === 'critico';
  const showInfoIcon = estado === 'info';

  const SEV_LABEL = { info: 'INFO', warning: 'BAJO UMBRAL', danger: 'CRÍTICO', critico: 'CRÍTICO' };
  const title =
    estado === 'sin_regla'
      ? 'Sin regla de alerta configurada'
      : umbral != null
      ? `Umbral mín ${umbral}${SEV_LABEL[estado] ? ` · ${SEV_LABEL[estado]}` : ''}`
      : SEV_LABEL[estado] || undefined;

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs',
        'cursor-pointer transition-opacity hover:opacity-80',
        STYLES[estado] || STYLES.sin_regla
      )}
    >
      {showInfoIcon && <Info className="w-3 h-3" />}
      {showWarnIcon && <AlertTriangle className="w-3 h-3" />}
      {showCritIcon && <ZapOff className="w-3 h-3" />}
      <span className="tabular-nums">{cantidad}</span>
    </button>
  );
}
